import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-user-profile-image',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container *ngIf="shouldShowPhoto(); else avatarFallback">
      <img
        [src]="photoURL"
        [alt]="displayName || 'User'"
        [style.width.px]="size"
        [style.height.px]="size"
        class="rounded-full object-cover"
        (error)="onImageError()"
        [title]="displayName || 'User'"
      />
    </ng-container>

    <ng-template #avatarFallback>
      <span
        class="relative flex items-center justify-center overflow-hidden rounded-full font-bold text-white select-none"
        [style.width.px]="size"
        [style.height.px]="size"
        [style.fontSize.px]="fontSize"
        [ngStyle]="{ 'background-color': backgroundColor }"
        [title]="titleText"
      >
        {{ initials }}
      </span>
    </ng-template>
  `,
  styles: [],
})
export class UserProfileImageComponent implements OnChanges {
  @Input() displayName: string | null | undefined = '';
  @Input() email: string | null | undefined = '';
  @Input() photoURL: string | null | undefined = null;
  @Input() size: number = 40; // Default size in pixels

  hasImageError: boolean = false;
  initials: string = '?';
  fontSize: number = 20; // Default font size
  backgroundColor: string = '#cccccc'; // Default background
  titleText: string = '';

  private colors: string[] = [
    '#F94144',
    '#F3722C',
    '#F8961E',
    '#F9C74F',
    '#90BE6D',
    '#43AA8B',
    '#4D908E',
    '#577590',
    '#277DA1',
    '#6a0dad', // Added purple
  ];

  ngOnChanges(changes: SimpleChanges): void {
    // Reset image error state when photoURL changes
    if (changes['photoURL']) {
      this.hasImageError = false;
    }

    this.updateAvatar();
  }

  onImageError(): void {
    this.hasImageError = true;
  }

  private updateAvatar(): void {
    const nameToUse = this.displayName?.trim() || this.email?.trim() || '';
    this.initials = this.getInitials(nameToUse);
    this.fontSize = Math.max(10, Math.floor(this.size * 0.4)); // Adjust font size based on avatar size
    this.backgroundColor = this.getColorFromName(nameToUse);
    this.titleText = nameToUse || 'User Avatar';
  }

  private getInitials(name: string): string {
    if (!name) return '?';
    const nameParts = name.split(' ').filter(part => part.length > 0);
    if (nameParts.length > 1) {
      return (
        nameParts[0][0] + nameParts[nameParts.length - 1][0]
      ).toUpperCase();
    } else if (name.length > 0) {
      // Try splitting by non-alphanumeric if it's likely an email before the @
      const emailParts = name.split(/[^a-zA-Z0-9]/);
      if (emailParts[0] && emailParts[0].length > 0) {
        return emailParts[0][0].toUpperCase();
      }
      return name[0].toUpperCase(); // Fallback to first character
    }
    return '?';
  }

  // Generate a consistent color based on the name
  private getColorFromName(name: string): string {
    if (!name) return this.colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash; // Convert to 32bit integer
    }
    const index = Math.abs(hash) % this.colors.length;
    return this.colors[index];
  }

  shouldShowPhoto(): boolean {
    console.debug('photoURL', this.photoURL);
    console.debug('hasImageError', this.hasImageError);
    if (this.photoURL && !this.hasImageError) {
      console.debug('shouldShowPhoto', true);
      return true;
    } else {
      return false;
    }
  }
}
